import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { PanelComponent }            from '@libs/front/components/panel/panel.component';
import { SelectComponent, SelectOption } from '@libs/front/components/select/select.component';
import { MetabaseDashboardComponent } from 'src/app/shared/components/metabase-dashboard/metabase-dashboard.component';
import { UtilisateurService }         from 'src/app/services/utilisateur.service';
import { DeviceService }              from 'src/app/services/device.service';
import { MetabaseService }            from 'src/app/services/metabase.service';

type SiteDashboardKind = 'site' | 'stevin-rock';

@Component({
  selector:    'app-site-dashboard',
  standalone:  true,
  imports:     [CommonModule, FormsModule, PanelComponent, SelectComponent, MetabaseDashboardComponent],
  templateUrl: './site-dashboard.component.html',
  styleUrls:   ['./site-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteDashboardComponent implements OnChanges {

  @Input() siteId: number | null = null;

  readonly utilService     = inject(UtilisateurService);
  readonly device          = inject(DeviceService);
  private readonly metabaseService = inject(MetabaseService);

  private readonly STEVIN_ROCK_SITE_IDS = [41, 594, 605];

  readonly dashboardOptions: SelectOption[] = [
    { id: 'site',        label: 'Site dashboard' },
    { id: 'stevin-rock', label: 'Stevin Rock' },
  ];

  private readonly currentSiteId = signal<number | null>(null);

  readonly canShowStevinRock = computed(() => {
    const siteId = this.currentSiteId();
    return siteId != null && this.STEVIN_ROCK_SITE_IDS.includes(siteId);
  });

  /**
   * Le dashboard du site lit `UtilisateurService.currentSite()` : on attend qu'il soit sur le site affiché
   * (sur un lien direct, `Sites/Active` n'est pas encore chargé) pour ne pas demander celui d'un autre site.
   */
  readonly currentSiteReady = computed(() => {
    const siteId = this.currentSiteId();
    return siteId != null && this.utilService.currentSite()?.siteId === siteId;
  });

  selectedDashboard = signal<SiteDashboardKind>('site');
  stevinRockUrl     = signal<string | undefined>(undefined);

  /** Contrôle le montage de app-metabase-dashboard pour forcer le rechargement de l'iframe */
  loadMetabase = signal(false);

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['siteId'] && this.siteId) {
      // Sync currentSite dans UtilisateurService pour que Metabase ait le bon siteId
      const site = this.utilService.listeSites.find(s => s.siteId === this.siteId);
      if (site) {
        this.utilService.setCurrentSite(site);
      }

      this.currentSiteId.set(this.siteId);
      this.stevinRockUrl.set(undefined);
      if (!this.canShowStevinRock()) {
        this.selectedDashboard.set('site');
      } else if (this.selectedDashboard() === 'stevin-rock') {
        this.loadStevinRockUrl();
      }

      this.reloadMetabase();
    }
  }

  /** Bascule entre le dashboard Metabase du site et celui de Stevin Rock */
  onDashboardChange(kind: SiteDashboardKind): void {
    if (kind === this.selectedDashboard()) return;
    this.selectedDashboard.set(kind);
    if (kind === 'stevin-rock' && !this.stevinRockUrl()) {
      this.loadStevinRockUrl();
    }
    this.reloadMetabase();
  }

  /** Récupère l'URL Metabase du dashboard Stevin Rock pour le site courant */
  private async loadStevinRockUrl(): Promise<void> {
    const siteId = this.currentSiteId();
    if (siteId == null) return;

    try {
      const url = await this.metabaseService.getMetabaseSiteUrl(siteId);
      if (this.currentSiteId() !== siteId) return;
      this.stevinRockUrl.set(url != null && url.trim().length > 0 ? url : undefined);
    } catch {
      this.stevinRockUrl.set(undefined);
    }
  }

  /** Démonte puis remonte app-metabase-dashboard pour recharger l'iframe */
  private reloadMetabase(): void {
    this.loadMetabase.set(false);
    setTimeout(() => this.loadMetabase.set(true), 100);
  }
}
